/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { CustomerCheckout } from "@lemmonhq/types";
import { GroupMember } from "../../../providers/Menu/realtime/index";
import { ConfirmFinalizationModal } from "./ConfirmFinalizationModal";
import { useMenu } from "../../../providers";
import { useLocalization } from "@lemmonhq/uikit";

export interface FinalizeButtonProps {
  onFinalize: () => void;
  isLoading?: boolean;
}

export const FinalizeButton = defineComponent<FinalizeButtonProps>({
  name: "FinalizeButton",
  props: ["onFinalize", "isLoading"],
  setup(props) {
    const { groupOrder, realtime } = useMenu();
    const { t } = useLocalization();
    const showConfirmModal = ref(false);

    // Get confirmed checkouts (ready to finalize)
    const confirmedCheckouts = computed(() => {
      const checkouts = groupOrder.value?.checkouts || [];
      return checkouts.filter(
        (checkout) => checkout.status === "confirmed" && !checkout.order_id,
      );
    });

    // Calculate pending members using same logic as PendingOrdersList
    const pendingMembers = computed(() => {
      const groupMembers = realtime.groupState.value.members as GroupMember[];
      return groupMembers.filter((member) => {
        const groupCheckouts = groupOrder.value?.checkouts || [];
        if (!groupCheckouts || groupCheckouts.length === 0) {
          return true;
        }
        const hasNonCompletedCheckout = groupCheckouts.some(
          (checkout) => checkout.customer_id === member.clientId,
        );
        return !hasNonCompletedCheckout;
      });
    });

    // All orders submitted when no pending members
    const allOrdersSubmitted = computed(() => {
      return pendingMembers.value.length === 0;
    });

    const handleButtonClick = () => {
      if (allOrdersSubmitted.value) {
        // All orders submitted, finalize directly
        props.onFinalize();
      } else {
        // Show confirmation modal
        showConfirmModal.value = true;
      }
    };

    const handleConfirm = () => {
      showConfirmModal.value = false;
      props.onFinalize();
    };

    const handleCancel = () => {
      showConfirmModal.value = false;
    };

    return () => (
      <div class="mb-8">
        {showConfirmModal.value && (
          <ConfirmFinalizationModal
            onClose={handleCancel}
            onConfirm={handleConfirm}
            isLoading={props.isLoading}
            groupMembers={realtime.groupState.value.members as GroupMember[]}
            submittedCheckouts={confirmedCheckouts.value}
          />
        )}

        <button
          class={`btn btn-lg btn-primary btn-block shadow-lg transition-all duration-300`}
          onClick={handleButtonClick}
          disabled={props.isLoading || confirmedCheckouts.value.length === 0}
        >
          {props.isLoading ? (
            <span class="loading loading-spinner loading-sm mr-2"></span>
          ) : null}
          {props.isLoading
            ? t("customer.checkout_status.processing")
            : t("customer.checkout_status.send_order")}
        </button>
      </div>
    );
  },
});
